import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useCallback,
  useRef,
  useState,
} from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { ShapeSelectionHistoryMeta } from '@/lib/shapes/types/shape-selection-history-meta'
import {
  buildForwardedSelectionHistoryMeta,
  inferBeforeTextIds,
  isDuplicateShapeSelectionReplay,
} from '../helpers/selection-history-decision'
import type { CenterCanvasConfig } from '../types/center-canvas-config'
import type {
  ForwardedShapeSelectionHistory,
  PendingSelectionHistoryMeta,
} from '../types/selection-history'

/** TextManager no longer renders (Phase 2), so selected text-box ids are always empty — a stable
 *  module-level reference so memo/callback deps don't see a "changed" array every render. */
const EMPTY_SELECTED_TEXT_BOX_IDS: string[] = []

/**
 * Canvas selection with undo history: the mirrored shape selection, selecting/clearing with a
 * history entry, forwarding ShapeManager's selection-history reports to the parent (deduped), and
 * group isolation mode (entering a group, leaving it, and the active isolation scope).
 */
export function useSelectionHistory({
  internalShapeManagerRef,
  setSelectedGuideIds,
  onBackgroundClick,
  onShapeSelectionHistory,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  setSelectedGuideIds: Dispatch<SetStateAction<Set<string>>>
  onBackgroundClick: CenterCanvasConfig['onBackgroundClick']
  onShapeSelectionHistory: CenterCanvasConfig['onShapeSelectionHistory']
}) {
  // Unified multi-select state: track selected text box IDs for cross-type selection.
  // TextManager no longer renders (Phase 2), so this is always empty — kept as a stable
  // reference (not a fresh `useState`) since nothing sets it anymore.
  const selectedTextBoxIds = EMPTY_SELECTED_TEXT_BOX_IDS
  const [selectedShapeIdsForUnified, setSelectedShapeIdsForUnified] = useState<string[]>([])
  const [groupIsolationPath, setGroupIsolationPath] = useState<string[]>([])
  const selectedTextBoxIdsRef = useRef(selectedTextBoxIds)
  const pendingSelectionHistoryMetaRef = useRef<PendingSelectionHistoryMeta | null>(null)
  const lastForwardedShapeSelectionHistoryRef = useRef<ForwardedShapeSelectionHistory | null>(null)

  selectedTextBoxIdsRef.current = selectedTextBoxIds

  const withPendingSelectionHistoryTextMeta = useCallback(
    (
      beforeTextIds: string[],
      afterTextIds: string[],
      apply: () => void,
      options?: Pick<ShapeSelectionHistoryMeta, 'beforeChrome' | 'afterChrome'>
    ) => {
      const pending = {
        beforeTextIds: [...beforeTextIds],
        afterTextIds: [...afterTextIds],
        beforeChrome: options?.beforeChrome,
        afterChrome: options?.afterChrome,
      }

      pendingSelectionHistoryMetaRef.current = pending
      apply()
    },
    []
  )

  const clearShapeSelectionWithHistory = useCallback(
    (afterTextIds: string[] = []) => {
      const mgr = internalShapeManagerRef.current

      if (!mgr) return
      if (mgr.getSelectedShapeIds().length === 0) {
        mgr.clearSelection({ recordHistory: true })
        return
      }

      withPendingSelectionHistoryTextMeta([], afterTextIds, () =>
        mgr.clearSelection({ recordHistory: true })
      )
    },
    [withPendingSelectionHistoryTextMeta, internalShapeManagerRef]
  )

  const clearUnifiedSelectionWithHistory = useCallback(() => {
    clearShapeSelectionWithHistory([])
    setSelectedGuideIds(new Set())
    onBackgroundClick?.()
  }, [clearShapeSelectionWithHistory, onBackgroundClick, setSelectedGuideIds])

  const enterGroupIsolationMode = useCallback(
    (groupId: string) => {
      const group = internalShapeManagerRef.current
        ?.getShapes()
        .find((shape) => shape.id === groupId)

      if (group?.locked === true) return
      internalShapeManagerRef.current?.recomputePassiveGroupBounds(groupId, {
        includeNested: true,
      })
      setGroupIsolationPath((prev) =>
        prev[prev.length - 1] === groupId ? prev : [...prev, groupId]
      )
      clearShapeSelectionWithHistory([])
      setSelectedGuideIds(new Set())
    },
    [clearShapeSelectionWithHistory, internalShapeManagerRef, setSelectedGuideIds]
  )

  const selectShapesWithHistoryAndTextMeta = useCallback(
    (nextShapeIds: string[], afterTextIds: string[]) => {
      const mgr = internalShapeManagerRef.current

      if (!mgr) return
      const beforeShapeIds = mgr.getSelectedShapeIds()
      const shapeSelectionWillChange =
        beforeShapeIds.length !== nextShapeIds.length ||
        beforeShapeIds.some((id) => !nextShapeIds.includes(id))

      if (!shapeSelectionWillChange) {
        mgr.selectShapesWithHistory(nextShapeIds)
        return
      }

      withPendingSelectionHistoryTextMeta([], afterTextIds, () =>
        mgr.selectShapesWithHistory(nextShapeIds)
      )
    },
    [withPendingSelectionHistoryTextMeta, internalShapeManagerRef]
  )

  const exitGroupIsolationMode = useCallback(() => {
    setGroupIsolationPath((prev) => {
      const rootId = prev[0]

      if (rootId) {
        internalShapeManagerRef.current?.recomputePassiveGroupBounds(rootId, {
          includeNested: true,
        })
      }

      return []
    })
    clearShapeSelectionWithHistory([])
    setSelectedGuideIds(new Set())
  }, [clearShapeSelectionWithHistory, internalShapeManagerRef, setSelectedGuideIds])

  const handleShapeSelectionHistoryFromShapeManager = useCallback(
    (beforeIds: string[], afterIds: string[], meta?: ShapeSelectionHistoryMeta) => {
      const pending = pendingSelectionHistoryMetaRef.current
      const lastForwarded = lastForwardedShapeSelectionHistoryRef.current
      const isDuplicateShapeReplayWithoutTextMeta = isDuplicateShapeSelectionReplay({
        pending,
        lastForwarded,
        beforeIds,
        afterIds,
        nowMs: Date.now(),
      })

      if (isDuplicateShapeReplayWithoutTextMeta) {
        pendingSelectionHistoryMetaRef.current = null
        return
      }

      const inferredBeforeTextIds = inferBeforeTextIds({
        pending,
        selectedTextBoxIds: selectedTextBoxIdsRef.current,
        beforeIds,
        afterIds,
      })

      pendingSelectionHistoryMetaRef.current = null
      lastForwardedShapeSelectionHistoryRef.current = {
        beforeIds: [...beforeIds],
        afterIds: [...afterIds],
        hadPendingTextMeta: pending != null || (inferredBeforeTextIds?.length ?? 0) > 0,
        atMs: Date.now(),
      }

      onShapeSelectionHistory?.(
        beforeIds,
        afterIds,
        buildForwardedSelectionHistoryMeta(meta, pending, inferredBeforeTextIds)
      )
    },
    [onShapeSelectionHistory]
  )

  const activeIsolationGroupId = groupIsolationPath[groupIsolationPath.length - 1] ?? null

  return {
    selectedTextBoxIdsRef,
    selectedShapeIdsForUnified,
    setSelectedShapeIdsForUnified,
    groupIsolationPath,
    setGroupIsolationPath,
    clearShapeSelectionWithHistory,
    clearUnifiedSelectionWithHistory,
    enterGroupIsolationMode,
    selectShapesWithHistoryAndTextMeta,
    exitGroupIsolationMode,
    handleShapeSelectionHistoryFromShapeManager,
    activeIsolationGroupId,
  }
}
